import { DOCS_FUNCTION_LIBRARY } from 'constants/Docs.constants';
import { codeInputPlaceholder } from 'constants/Functions.constants';

import {
  END_FUNCTION_NAME,
  START_FUNCTION_NAME,
} from '@polyai/stream/commands/handlers/special-functions/constants';
import type { FunctionStep } from '@polyai/stream/store/reducers/flows';
import { delayResponseReferencesAdapter } from '@polyai/stream/store/reducers/functions/adapters';
import {
  delayResponsesSelectors,
  parametersSelectors,
} from '@polyai/stream/store/reducers/functions/selectors';
import type { Function } from '@polyai/stream/store/reducers/functions/types';
import type { SpecialFunction } from '@polyai/stream/store/reducers/specialFunctions/types';
import { getPromptRefs } from '@polyai/stream/utils/get-prompt-refs';
import { LaunchIcon } from '@polyai/ui/components/atoms/Icons';

import type { FunctionType, ParameterType } from 'api/types';
import {
  generateDelayResponseId,
  generateFunctionId,
  generateParameterId,
  generateTransitionId,
} from 'lib/prettyUuid';
import { getFuncArgs } from 'utils/functions';
import * as Styled from './FunctionForm.styled';
import type {
  StreamDelayResponse,
  StreamFunctionForm,
  StreamFunctionLatencyControl,
  StreamFunctionParameter,
} from './types';

export const newParameter = (): StreamFunctionParameter => ({
  id: generateParameterId(),
  name: '',
  description: '',
  type: 'string',
});

export const newDelayResponse = (): StreamDelayResponse => ({
  id: generateDelayResponseId(),
  duration: 3,
  message: '',
});

export const getFunctionName = (name: string, functionType?: FunctionType) => {
  switch (functionType) {
    case 'conversation_start':
      return 'start_function';
    case 'conversation_end':
      return 'end_function';
  }
  return name;
};

export const getFunctionType = (functionName: string) => {
  switch (functionName) {
    case START_FUNCTION_NAME:
      return 'conversation_start';
    case END_FUNCTION_NAME:
      return 'conversation_end';
  }
  return null;
};

export const getLatencyControlDefaultValues = (
  func: Omit<Function, 'references'> | FunctionStep['function'] | undefined,
): StreamFunctionLatencyControl => {
  const payload = func?.latencyControl
    ? {
        enabled: func.latencyControl.enabled,
        initialDelay: func.latencyControl.initialDelay,
        interval: func.latencyControl.interval,
        delayResponses: delayResponsesSelectors
          .selectAll(func.latencyControl.delayResponses)
          .map(
            ({
              createdAt: _,
              createdBy: __,
              updatedAt: ___,
              updatedBy: ____,
              ...delayResponse
            }) => ({ ...delayResponse }),
          ),
      }
    : {
        enabled: false,
        initialDelay: 0,
        interval: 0,
        delayResponses: [],
      };

  if (
    !payload.delayResponses.length ||
    payload.delayResponses.at(-1)?.message !== ''
  ) {
    payload.delayResponses.push(newDelayResponse());
  }

  return payload;
};

export const newFunction = (
  name: string,
  functionType?: FunctionType,
  defaultDescription: string = '',
): StreamFunctionForm => ({
  id:
    functionType === 'flow_function'
      ? generateTransitionId()
      : generateFunctionId(),
  name: getFunctionName(name, functionType),
  description: defaultDescription,
  parameters: [newParameter()],
  code: codeInputPlaceholder(
    getFunctionName(name, functionType),
    getFuncArgs(functionType),
  ),
  errors: [],
  archived: false,
});

export const functionDefTooltip = (
  <span>
    Define your function definition here. Import common libraries, have a look
    at our{' '}
    <Styled.LibrariesLink to={DOCS_FUNCTION_LIBRARY}>
      full list here <LaunchIcon />
    </Styled.LibrariesLink>
  </span>
);

export const typeToPython = (paramType: ParameterType) => {
  switch (paramType) {
    case 'string':
      return 'str';
    case 'integer':
      return 'int';
    case 'number':
      // Switch when typing auto imported
      // return 'typing.Union[int, float]';
      return 'float';
    case 'boolean':
      return 'bool';
  }
};

export const parseFunction = (
  func: Function | SpecialFunction,
  functionType: FunctionType | undefined,
): StreamFunctionForm => {
  const {
    createdAt: _ca,
    createdBy: _cb,
    updatedAt: _ua,
    updatedBy: _ub,
    ...rest
  } = func;

  // Narrow types: start/end are SpecialFunction, others are Global Functions
  const functionData =
    functionType === 'conversation_start' || functionType === 'conversation_end'
      ? rest // SpecialFunction: no references to drop
      : (() => {
          const { references: _r, ...withoutRefs } = rest as Function;
          return withoutRefs;
        })();

  let newFunc: any = {
    ...functionData,
    parameters: parametersSelectors
      .selectAll(func.parameters)
      ?.map(
        ({
          createdAt: _ca,
          createdBy: _cb,
          updatedAt: _ua,
          updatedBy: _ub,
          ...param
        }) => ({
          ...param,
          type: param.type as 'string' | 'number' | 'boolean',
        }),
      ),
  };

  if (functionType === undefined || functionType === 'flow_function') {
    // We know this is a global function
    const globalFunc = func as Function;
    newFunc = {
      ...newFunc,
      latencyControl: globalFunc.latencyControl
        ? {
            enabled: globalFunc.latencyControl.enabled,
            initialDelay: globalFunc.latencyControl.initialDelay,
            interval: globalFunc.latencyControl.interval,
            delayResponses: delayResponsesSelectors
              .selectAll(globalFunc.latencyControl.delayResponses)
              .map(
                ({
                  createdAt: _ca,
                  createdBy: _cb,
                  updatedAt: _ua,
                  updatedBy: _ub,
                  ...delayResponse
                }) => ({ ...delayResponse }),
              ),
          }
        : {
            enabled: false,
            initialDelay: 0,
            interval: 0,
            delayResponses: [],
          },
    };
  }

  if (!newFunc.parameters.length || newFunc.parameters.at(-1)?.name !== '') {
    newFunc.parameters.push(newParameter());
  }

  return newFunc;
};

export const getDefaultValues = ({
  func,
  defaultName = 'function_1',
  functionType,
  defaultDescription = '',
}: {
  func?: Function | SpecialFunction;
  defaultName?: string;
  functionType?: FunctionType;
  defaultDescription?: string;
}) => {
  return func
    ? parseFunction(func, functionType)
    : newFunction(defaultName, functionType, defaultDescription);
};

function generateFunctionSignature(
  functionName: string,
  parameters: StreamFunctionParameter[],
): string {
  const args: string[] = ['conv: Conversation'];

  parameters.forEach(param => {
    const pythonType = typeToPython(param.type);
    args.push(`${param.name}: ${pythonType}`);
  });

  return `def ${functionName}(${args.join(', ')}):`;
}

export function updateFunctionSignatureInCode(
  code: string,
  functionName: string,
  newParameters: StreamFunctionParameter[],
): string {
  if (!code) {
    return code;
  }

  const newSignature = generateFunctionSignature(functionName, newParameters);
  const escapedFunctionName = functionName.replace(
    /[.*+?^${}()|[\]\\]/g,
    '\\$&',
  );
  const functionDefPattern = new RegExp(
    `^(def\\s+${escapedFunctionName}\\s*\\([^)]*\\)\\s*:)`,
    'm',
  );

  const match = code.match(functionDefPattern);

  if (match) {
    return code.replace(functionDefPattern, newSignature);
  }

  return code;
}

export const formatPostFunction = (functionData: StreamFunctionForm) => {
  functionData.parameters = functionData?.parameters?.filter(p => !!p.name);

  return functionData;
};

export const validateParameterNames = (
  parameters: StreamFunctionParameter[],
) => {
  const parameterNames = parameters
    .filter(param => !!param.name)
    .map(param => param.name);
  if (new Set(parameterNames).size !== parameterNames.length) {
    return 'Parameter names must be unique';
  }
  return true;
};

export const formatLatencyControl = (data: StreamFunctionLatencyControl) => {
  // Last delay response input is always an empty one
  const { delayResponses } = data;
  const isLastDelayResponseDummy =
    delayResponses.length && !delayResponses[delayResponses.length - 1].message;

  if (isLastDelayResponseDummy) {
    data.delayResponses = delayResponses.slice(0, -1);
  }

  // Parse references for each delay response message
  data.delayResponses = data.delayResponses.map(delayResponse => {
    const references = getPromptRefs(
      delayResponse.message,
      delayResponseReferencesAdapter.createInitialState(),
    );
    return {
      ...delayResponse,
      references,
    };
  });

  return data;
};

export function isValidPythonParamName(name: string): boolean {
  if (typeof name !== 'string' || name.length === 0) {
    return false;
  }

  const re: RegExp = /^[A-Za-z_][A-Za-z0-9_]*$/;

  return re.test(name);
}

export const extractVariableNames = (code: string): string[] => {
  // Searching for 'conv.state.variableName'
  const convStatePattern =
    /(?<![\w\.])conv\.state\.([a-zA-Z_][a-zA-Z0-9_]*)\b(?!\s*\()/g;

  const variableNames: string[] = [];
  let match;
  while ((match = convStatePattern.exec(code)) !== null) {
    const variableName = match[1];
    if (!variableNames.includes(variableName)) {
      variableNames.push(variableName);
    }
  }
  return variableNames;
};
